import Head from "next/head";
import PropTypes from "prop-types";

const FV_FALLBACK_IMAGE = "https://www.fundview.com/fv_seo_icon.png";
const FV_FALLBACK_WIDTH = 585;
const FV_FALLBACK_HEIGHT = 593;
const ORG_LOGO_DEFAULT_SIZE = 280;
const SITE_NAME = "Fundview";

/**
 * Renders a complete set of SEO meta tags via next/head for maximum
 * cross-platform link-preview compatibility (iMessage, Slack, LinkedIn,
 * Twitter/X, Facebook, Discord, WhatsApp).
 */
export default function SeoHead({ organization, canonicalUrl }) {
  const orgName = organization?.organizationName || "";
  const orgLogo = organization?.logoUrl || "";
  const title = orgName ? `Loan Application \u2014 ${orgName}` : "Loan Application";
  const description = orgName
    ? `Submit a loan application with ${orgName}`
    : "Submit a loan application";

  const hasOrgLogo = Boolean(orgLogo);
  const imageUrl = hasOrgLogo ? orgLogo : FV_FALLBACK_IMAGE;
  const imageAlt = hasOrgLogo ? `${orgName} logo` : `${SITE_NAME} logo`;
  const imageWidth = hasOrgLogo ? ORG_LOGO_DEFAULT_SIZE : FV_FALLBACK_WIDTH;
  const imageHeight = hasOrgLogo ? ORG_LOGO_DEFAULT_SIZE : FV_FALLBACK_HEIGHT;

  return (
    <Head>
      <title key="title">{title}</title>
      <meta key="description" name="description" content={description} />
      {canonicalUrl && <link key="canonical" rel="canonical" href={canonicalUrl} />}

      {/* Open Graph — LinkedIn, Facebook, iMessage, Slack, Discord, WhatsApp */}
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:image" property="og:image" content={imageUrl} />
      <meta key="og:image:alt" property="og:image:alt" content={imageAlt} />
      <meta key="og:image:width" property="og:image:width" content={String(imageWidth)} />
      <meta key="og:image:height" property="og:image:height" content={String(imageHeight)} />
      <meta key="og:image:type" property="og:image:type" content="image/png" />
      {canonicalUrl && <meta key="og:url" property="og:url" content={canonicalUrl} />}
      <meta key="og:site_name" property="og:site_name" content={SITE_NAME} />
      <meta key="og:locale" property="og:locale" content="en_US" />

      {/* Twitter Card — Twitter/X, Slack fallback, iMessage fallback */}
      <meta key="twitter:card" name="twitter:card" content="summary" />
      <meta key="twitter:title" name="twitter:title" content={title} />
      <meta key="twitter:description" name="twitter:description" content={description} />
      <meta key="twitter:image" name="twitter:image" content={imageUrl} />
      <meta key="twitter:image:alt" name="twitter:image:alt" content={imageAlt} />

      {/* Apple — iMessage, Safari */}
      <meta key="apple-mobile-web-app-title" name="apple-mobile-web-app-title" content={orgName || SITE_NAME} />
    </Head>
  );
}

SeoHead.propTypes = {
  organization: PropTypes.shape({
    organizationName: PropTypes.string,
    logoUrl: PropTypes.string,
  }),
  canonicalUrl: PropTypes.string,
};
